<script setup lang="ts">
import type { StyleValue } from "vue"

const props = defineProps({
  flowX: null,
  flowY: null,
  anchorX: null,
  anchorY: null,
  openOnMount: { type: Boolean }
})
const active = defineModel<boolean>("active")
const dropdown = useTemplateRef("dropdown")
const overlay = useTemplateRef("overlay")
const { navIgnoreMainViewGestures } = useGlobals()

/**
 * This attaches a global listener that detects whether we have clicked
 * outside the dropdown menu. This is tricky since the dropdown might
 * open submenus that are not children of the dropdown itself (we teleport
 * all dropdowns to the body in order to avoid children placement bugs).
 * So we need to not only check whether the clicked element is a child of
 * our dropdown or dropdown menu, but also if we contain any of the classes
 * we want to ignore
 */
function handleClickOutside(event: MouseEvent) {
  if (!event.target)
    return
  if (event.target !== overlay.value)
    return

  if (active.value)
    active.value = false
}

function open() {
  active.value = true
}
function close() {
  active.value = false
}
function toggle() {
  if (active.value)
    close()
  else open()
}

// NOTE @abhay-agarwal this for some reason doesn't work with a computed variable, so we
// have to use a watch instead
const style: Ref<StyleValue> = ref()
watch(active, (active) => {
  const styleValue: StyleValue = {}
  if (!dropdown.value || !active)
    return

  const page = document.body.getBoundingClientRect()
  const { left, top, height, width } = dropdown.value.getBoundingClientRect()
  if (props.anchorY === "top") {
    styleValue.top = px(top)
    if (props.flowY === "down")
      styleValue.maxHeight = px(page.height - top)
    else
      styleValue.maxHeight = px(top)
  }
  else {
    styleValue.top = px(top + height)
    if (props.flowY === "down")
      styleValue.maxHeight = px(page.height - top - height)
    else
      styleValue.maxHeight = px(top + height)
  }
  if (props.anchorX === "left")
    styleValue.left = px(left)
  else
    styleValue.left = px(left + width)

  // down + right side requires no transform
  if (props.flowY === "up" && props.flowX === "right")
    styleValue.transform = "translate(0, -100%)"
  else if (props.flowY === "up" && props.flowX === "left")
    styleValue.transform = "translate(-100%, -100%)"
  else if (props.flowY === "down" && props.flowX === "left")
    styleValue.transform = "translate(-100%, 0)"

  style.value = styleValue
})

onMounted(() => {
  if (props.openOnMount)
    active.value = true
})
</script>

<template>
  <div ref="dropdown" class="relative inline-block text-left max-w-full">
    <div>
      <slot
        :open
        :toggle
        :close
        :active
      />
      <div
        v-if="active"
        ref="overlay"
        class="overlay"
        :class="[navIgnoreMainViewGestures]"
        @mousedown="e => handleClickOutside(e)"
      />
    </div>
    <teleport to="body">
      <transition
        enter-active-class="transition duration-60 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-60 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <div
          v-if="active"
          class="dropdown-menu-frame"
          :style
        >
          <slot
            name="menu"
            :active
            :open
            :toggle
            :close
          />
        </div>
      </transition>
    </teleport>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'
.bottom-left
  @apply mt-3 top-[100%] left-0 origin-top-left
.bottom-right
  @apply mt-3 top-[100%] right-0 origin-top-right
.left-top
  @apply ml-3 left-[100%] top-0 origin-top-left

.overlay
  @apply fixed top-0 left-0 right-0 bottom-0 pointer-events-auto z-under-overlay-layer

.dropdown-menu-frame
  @apply absolute z-overlay-layer flex flex-col
</style>
